/**
 * Rename Logic Hook
 *
 * Shared hook for inline rename functionality in workspace items.
 * Used by both WorkspaceItemCard (grid view) and ListTableRow (list view).
 *
 * @module features/workspace/hooks/useRenameLogic
 */

import { useCallback, useRef, useEffect } from 'react';
import type { WorkspaceItemType } from '@loader/shared-types';

import { useRenameStore } from '../stores';
import { useRenameTableMutation, useRenameLinkedTableMutation } from './useRenameMutations';

import { useUpdateFolderMutation } from '@/features/organization/hooks';
import { toast } from '@/lib/toast';

/**
 * Hook return type for rename functionality
 */
export interface UseRenameLogicReturn {
  /** Ref for the input element (for focus management) */
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** Whether this item is currently being renamed */
  isRenaming: boolean;
  /** Current rename value (temporary while editing) */
  renameValue: string;
  /** Update the rename value */
  setRenameValue: (value: string) => void;
  /** Start rename mode for this item */
  handleStartRename: () => void;
  /** Save the rename (submit the mutation) */
  handleSaveRename: () => void;
  /** Handle keyboard events (Enter to save, Escape to cancel) */
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Custom hook for rename functionality
 *
 * Consolidates rename logic to avoid duplication between grid and list views.
 * Handles:
 * - Rename state management via useRenameStore
 * - API mutations for tables, linked tables, and folders
 * - Input focus management
 * - Keyboard shortcuts (Enter/Escape)
 * - Toast notifications for success/error
 *
 * @param id - The item ID
 * @param type - The item type (table, linked_table, or folder)
 * @param name - The current name of the item
 * @param canEdit - Whether user can edit (editor/owner) - viewers cannot rename
 */
export function useRenameLogic(
  id: string,
  type: WorkspaceItemType,
  name: string,
  canEdit = true
): UseRenameLogicReturn {
  const inputRef = useRef<HTMLInputElement>(null);

  // Rename state from store
  const { renamingItemId, renameValue, startRename, setRenameValue, cancelRename } =
    useRenameStore();
  const isRenaming = renamingItemId === id;

  // Mutations
  const renameTableMutation = useRenameTableMutation();
  const renameLinkedTableMutation = useRenameLinkedTableMutation();
  const updateFolderMutation = useUpdateFolderMutation();

  // Focus input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  // Start rename mode - disabled for viewers
  const handleStartRename = useCallback(() => {
    if (!canEdit) return;
    startRename(id, type, name);
  }, [id, type, name, startRename, canEdit]);

  // Save rename
  const handleSaveRename = useCallback(() => {
    const trimmedName = renameValue.trim();

    // Validation
    if (!trimmedName) {
      toast.workspace.emptyName();
      return;
    }
    if (trimmedName === name) {
      cancelRename();
      return;
    }

    // Mutation options - API returns descriptive errors like:
    // - "A folder named 'X' already exists in this location"
    // - "A table named 'X' already exists"
    // Success has immediate visual feedback (inline rename shows change instantly)
    // Only show toast on error
    const mutateOptions = {
      onSuccess: () => {
        cancelRename();
      },
      onError: (err: Error) => {
        toast.workspace.renameFailed(err.message);
      },
    };

    // Execute appropriate mutation based on type
    if (type === 'table') {
      renameTableMutation.mutate({ id, name: trimmedName }, mutateOptions);
    } else if (type === 'linked_table') {
      renameLinkedTableMutation.mutate({ id, name: trimmedName }, mutateOptions);
    } else if (type === 'folder') {
      updateFolderMutation.mutate({ id, data: { name: trimmedName } }, mutateOptions);
    }
  }, [
    id,
    type,
    name,
    renameValue,
    cancelRename,
    renameTableMutation,
    renameLinkedTableMutation,
    updateFolderMutation,
  ]);

  // Handle keyboard events in rename input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveRename();
      } else if (e.key === 'Escape') {
        cancelRename();
      }
    },
    [handleSaveRename, cancelRename]
  );

  return {
    inputRef,
    isRenaming,
    renameValue,
    setRenameValue,
    handleStartRename,
    handleSaveRename,
    handleKeyDown,
  };
}
